// T2.4 (GGOP-S 3.3.x–3.6.x): the genoptræningsplan form's state and the plan body sent
// to communicate-ggop "build" (Communicate ref-preview "ggop" → buildGgop). The
// mandatory-field table, labels, limits and the extended-ability template come from
// Communicate ("form"), so the form blocks exactly what the builder enforces.

export type GgopPlanType = 'almen' | 'specialiseret' | 'rehabilitering';
export const GGOP_PLAN_TYPES: readonly GgopPlanType[] = ['almen', 'specialiseret', 'rehabilitering'];

export type GgopFormMeta = {
  planTypes: { planType: GgopPlanType; statisticalCode: string; heading: string }[];
  requiredFields: Record<GgopPlanType, string[]>;
  fieldLabels: Record<string, string>;
  abilityTemplate: { heading: string; functions: string[] }[];
  relationCodes: string[];
  identifierCodes: string[];
  phoneCodes: string[];
  limits: {
    ccReceivers: number;
    ccReceiversWithGp: number;
    journalExcerpts: number;
    healthCareTeam: number;
    diagnosisText: number;
    phones: number;
  };
};

export type GgopPartyDraft = {
  ean: string;
  identifier: string;
  identifierCode: string;
  organisationName: string;
  departmentName?: string;
  streetName?: string;
  districtName?: string;
  postCodeIdentifier?: string;
};
/** No employee id: SignedByType has no IdentifierCode for a person (MedCom decision, known limitation). */
export type GgopPersonDraft = {
  givenName: string;
  surname: string;
  title: string;
  phone: string;
  departmentName: string;
};
export type GgopPhoneDraft = { number: string; code: string };
export type GgopAddressDraft = {
  givenName: string;
  surname: string;
  streetName: string;
  suburbName: string;
  districtName: string;
  postCodeIdentifier: string;
};
export type GgopRelativeDraft = GgopAddressDraft & { relationCode: string; phones: GgopPhoneDraft[]; email: string };
export type GgopContactDraft = {
  ean: string;
  departmentName: string;
  streetName: string;
  districtName: string;
  postCodeIdentifier: string;
  phone: string;
};
export type GgopDiagnosisDraft = { code: string; text: string };
export type GgopExcerptDraft = { entryId: string; typeOfExcerpt: string; signer: GgopPersonDraft | null; date: string; comment: string };

export type GgopFormState = {
  planType: GgopPlanType;
  receiver: GgopPartyDraft | null;
  gpCopy: boolean;
  gpCcReceiver: GgopPartyDraft | null;
  ccReceivers: GgopPartyDraft[];
  patient: {
    givenName: string;
    surname: string;
    streetName: string;
    suburbName: string;
    districtName: string;
    postCodeIdentifier: string;
    occupation: string;
    email: string;
    phones: GgopPhoneDraft[];
    noTelephoneReason: string;
    coAddressOn: boolean;
    coAddress: GgopAddressDraft;
  };
  relativeMode: 'ikke_relevant' | 'relative';
  relative: GgopRelativeDraft;
  signedBy: GgopPersonDraft;
  coWriterOn: boolean;
  coWriter: GgopPersonDraft;
  informationForPatientAndRelatives: string;
  consentInformed: boolean;
  practicalInformation: string;
  patientSafetyAspects: string;
  rehabilitationDeadline: string;
  contactStartDate: string;
  contactStartTime: string;
  contactEndMode: 'date' | 'text';
  contactEndDate: string;
  contactEndTime: string;
  contactEndText: string;
  controlMode: 'none' | 'date' | 'text';
  controlDate: string;
  controlTime: string;
  controlText: string;
  controlLocation: GgopPartyDraft | null;
  patientHealthSummary: string;
  abilityAtDischargeSummary: string;
  extendedOn: boolean;
  abilityAtDischargeExtended: Record<string, string>;
  rehabilitationNeedAndPotential: string;
  icdMode: 'coded' | 'text';
  icdPrimary: GgopDiagnosisDraft | null;
  icdSecondary: GgopDiagnosisDraft[];
  icdText: string;
  icf: { code: string; text: string; gradient: string }[];
  journalExcerpts: GgopExcerptDraft[];
  contactMunicipality: GgopContactDraft;
  contactCountyOn: boolean;
  contactCounty: GgopContactDraft;
  healthCareTeam: GgopPersonDraft[];
};

export const emptyPerson = (): GgopPersonDraft => ({ givenName: '', surname: '', title: '', phone: '', departmentName: '' });
export const emptyAddress = (): GgopAddressDraft => ({ givenName: '', surname: '', streetName: '', suburbName: '', districtName: '', postCodeIdentifier: '' });
export const emptyContact = (): GgopContactDraft => ({ ean: '', departmentName: '', streetName: '', districtName: '', postCodeIdentifier: '', phone: '' });

/** ICF qualifier (gradient) values 0–4, 8, 9 with their Danish texts. */
export const ICF_GRADIENTS: readonly { code: string; text: string }[] = [
  { code: '0', text: 'Intet problem' },
  { code: '1', text: 'Let problem' },
  { code: '2', text: 'Moderat problem' },
  { code: '3', text: 'Svært problem' },
  { code: '4', text: 'Totalt problem' },
  { code: '8', text: 'Ikke specificeret' },
  { code: '9', text: 'Ikke relevant' },
];

/** Danish text per RelationCode (NSK 1.0.4), as MedCom's stylesheet shows them. */
export const GGOP_RELATION_LABELS: Record<string, string> = {
  uspec_paaroerende: 'Uspecificeret',
  barn: 'Barn',
  aegtefaelle: 'Ægtefælle',
  'forældre': 'Forældre',
  barnebarn: 'Barnebarn',
  svigerbarn: 'Svigerbarn',
  ingen_relationer: 'Ingen relationer',
  nabo: 'Nabo',
  samboende: 'Samboende',
  registreret_partner: 'Registreret partner',
  'søskende': 'Søskende',
  'øvrig_familie': 'Øvrig familie',
  vaerge: 'Værge',
};

const filled = (v: string | null | undefined): boolean => typeof v === 'string' && v.trim().length > 0;
const opt = (v: string | null | undefined): string | undefined => (filled(v) ? (v as string).trim() : undefined);

/** "Fornavn Mellemnavn Efternavn" → given + surname (the last word). */
export function splitName(name: string | null | undefined): { givenName: string; surname: string } {
  const parts = (name ?? '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return { givenName: '', surname: '' };
  if (parts.length === 1) return { givenName: '', surname: parts[0] };
  return { givenName: parts.slice(0, -1).join(' '), surname: parts[parts.length - 1] };
}

/** Most extra copy receivers allowed now (GGOP-S 3.4.5/3.4.6). */
export function ggopCcMax(meta: Pick<GgopFormMeta, 'limits'>, gpCopy: boolean): number {
  return gpCopy ? meta.limits.ccReceiversWithGp : meta.limits.ccReceivers;
}

function party(p: GgopPartyDraft) {
  return {
    ean: p.ean.trim(),
    identifier: p.identifier.trim(),
    identifierCode: p.identifierCode.trim(),
    organisationName: p.organisationName.trim(),
    ...(opt(p.departmentName) ? { departmentName: opt(p.departmentName) } : {}),
    ...(opt(p.streetName) ? { streetName: opt(p.streetName) } : {}),
    ...(opt(p.districtName) ? { districtName: opt(p.districtName) } : {}),
    ...(opt(p.postCodeIdentifier) ? { postCodeIdentifier: opt(p.postCodeIdentifier) } : {}),
  };
}
function person(p: GgopPersonDraft) {
  return {
    givenName: p.givenName.trim(),
    surname: p.surname.trim(),
    title: p.title.trim(),
    ...(opt(p.phone) ? { phone: opt(p.phone) } : {}),
    ...(opt(p.departmentName) ? { departmentName: opt(p.departmentName) } : {}),
  };
}
function phones(list: GgopPhoneDraft[], max: number) {
  return list.filter((p) => filled(p.number)).slice(0, max).map((p) => ({ number: p.number.trim(), code: p.code }));
}
function contact(c: GgopContactDraft) {
  return {
    ...(opt(c.ean) ? { ean: opt(c.ean) } : {}),
    departmentName: c.departmentName.trim(),
    streetName: c.streetName.trim(),
    districtName: c.districtName.trim(),
    postCodeIdentifier: c.postCodeIdentifier.trim(),
    phone: c.phone.trim(),
  };
}

export type GgopSender = {
  ean: string;
  identifier: string;
  identifierCode: string;
  organisationName: string;
  streetName?: string;
  districtName?: string;
  postCodeIdentifier?: string;
  telephoneSubscriberIdentifier?: string;
};

/**
 * The plan body for communicate-ggop "build". cpr is the patient's CPR read via
 * decrypt-cpr (10 digits); it is passed through and never kept by the form.
 */
export function ggopPlanBody(state: GgopFormState, ctx: { sender: GgopSender; cpr: string | null; meta: GgopFormMeta }) {
  const { sender, cpr, meta } = ctx;
  const p = state.patient;
  const ccMax = ggopCcMax(meta, state.gpCopy);
  const extended = state.extendedOn || state.planType === 'rehabilitering';
  const icd10 =
    state.icdMode === 'text'
      ? filled(state.icdText) ? { text: state.icdText.trim() } : undefined
      : state.icdPrimary && filled(state.icdPrimary.code)
        ? {
            primary: { code: state.icdPrimary.code.trim(), text: state.icdPrimary.text },
            secondary: state.icdSecondary.filter((d) => filled(d.code)).map((d) => ({ code: d.code.trim(), text: d.text })),
          }
        : undefined;
  return {
    planType: state.planType,
    sender,
    signedBy: person(state.signedBy),
    ...(state.coWriterOn && filled(state.coWriter.surname) ? { coWriter: person(state.coWriter) } : {}),
    ...(state.receiver ? { receiver: party(state.receiver) } : {}),
    gpCcReceiver: state.gpCopy && state.gpCcReceiver ? party(state.gpCcReceiver) : null,
    ccReceivers: state.ccReceivers.slice(0, ccMax).map(party),
    patient: {
      ...(cpr ? { cpr } : {}),
      surname: p.surname.trim(),
      ...(opt(p.givenName) ? { givenName: opt(p.givenName) } : {}),
      ...(opt(p.streetName) ? { streetName: opt(p.streetName) } : {}),
      ...(opt(p.suburbName) ? { suburbName: opt(p.suburbName) } : {}),
      ...(opt(p.districtName) ? { districtName: opt(p.districtName) } : {}),
      ...(opt(p.postCodeIdentifier) ? { postCodeIdentifier: opt(p.postCodeIdentifier) } : {}),
      ...(opt(p.occupation) ? { occupation: opt(p.occupation) } : {}),
      ...(opt(p.email) ? { email: opt(p.email) } : {}),
      phones: phones(p.phones, meta.limits.phones),
      ...(phones(p.phones, meta.limits.phones).length === 0 && opt(p.noTelephoneReason) ? { noTelephoneReason: opt(p.noTelephoneReason) } : {}),
      ...(p.coAddressOn && filled(p.coAddress.streetName)
        ? {
            coAddress: {
              ...(opt(p.coAddress.givenName) ? { givenName: opt(p.coAddress.givenName) } : {}),
              ...(opt(p.coAddress.surname) ? { surname: opt(p.coAddress.surname) } : {}),
              streetName: p.coAddress.streetName.trim(),
              ...(opt(p.coAddress.suburbName) ? { suburbName: opt(p.coAddress.suburbName) } : {}),
              districtName: p.coAddress.districtName.trim(),
              postCodeIdentifier: p.coAddress.postCodeIdentifier.trim(),
            },
          }
        : {}),
    },
    relatives:
      state.relativeMode === 'relative' && filled(state.relative.surname) && filled(state.relative.relationCode)
        ? [
            {
              relationCode: state.relative.relationCode,
              surname: state.relative.surname.trim(),
              ...(opt(state.relative.givenName) ? { givenName: opt(state.relative.givenName) } : {}),
              ...(opt(state.relative.streetName) ? { streetName: opt(state.relative.streetName) } : {}),
              ...(opt(state.relative.suburbName) ? { suburbName: opt(state.relative.suburbName) } : {}),
              ...(opt(state.relative.districtName) ? { districtName: opt(state.relative.districtName) } : {}),
              ...(opt(state.relative.postCodeIdentifier) ? { postCodeIdentifier: opt(state.relative.postCodeIdentifier) } : {}),
              phones: phones(state.relative.phones, meta.limits.phones),
              ...(opt(state.relative.email) ? { email: opt(state.relative.email) } : {}),
            },
          ]
        : [],
    informationForPatientAndRelatives: state.informationForPatientAndRelatives,
    consentInformed: state.consentInformed,
    ...(opt(state.practicalInformation) ? { practicalInformation: state.practicalInformation } : {}),
    ...(opt(state.patientSafetyAspects) ? { patientSafetyAspects: state.patientSafetyAspects } : {}),
    ...(opt(state.rehabilitationDeadline) ? { rehabilitationDeadline: state.rehabilitationDeadline } : {}),
    currentHospitalContact: {
      start: { date: state.contactStartDate, time: state.contactStartTime },
      ...(state.contactEndMode === 'date' && filled(state.contactEndDate)
        ? { expectedEnd: { date: state.contactEndDate, time: state.contactEndTime || '00:00' } }
        : {}),
      ...(state.contactEndMode === 'text' && filled(state.contactEndText) ? { expectedEndText: state.contactEndText.trim() } : {}),
    },
    ...(state.controlMode !== 'none' || state.controlLocation
      ? {
          hospitalAmbulatoryControl: {
            ...(state.controlMode === 'date' && filled(state.controlDate)
              ? { expectedStart: { date: state.controlDate, time: state.controlTime || '00:00' } }
              : {}),
            ...(state.controlMode === 'text' && filled(state.controlText) ? { expectedStartText: state.controlText.trim() } : {}),
            ...(state.controlLocation ? { location: party(state.controlLocation) } : {}),
          },
        }
      : {}),
    patientHealthSummary: state.patientHealthSummary,
    abilityAtDischargeSummary: state.abilityAtDischargeSummary,
    ...(extended && Object.values(state.abilityAtDischargeExtended).some(filled)
      ? { abilityAtDischargeExtended: state.abilityAtDischargeExtended }
      : {}),
    rehabilitationNeedAndPotential: state.rehabilitationNeedAndPotential,
    ...(icd10 ? { icd10 } : {}),
    icf: state.icf
      .filter((c) => filled(c.code))
      .map((c) => {
        const gradient = ICF_GRADIENTS.find((g) => g.code === c.gradient);
        return { code: { code: c.code.trim(), text: c.text.trim() }, ...(gradient ? { gradient: { code: gradient.code, text: gradient.text } } : {}) };
      }),
    journalExcerpts: state.journalExcerpts.slice(0, meta.limits.journalExcerpts).map((j) => ({
      typeOfExcerpt: j.typeOfExcerpt.trim(),
      ...(j.signer && filled(j.signer.surname) ? { signer: person(j.signer) } : {}),
      ...(filled(j.date) ? { date: j.date } : {}),
      comment: j.comment,
    })),
    contactMunicipality: contact(state.contactMunicipality),
    ...(state.contactCountyOn ? { contactCounty: contact(state.contactCounty) } : {}),
    healthCareTeam: state.healthCareTeam.filter((m) => filled(m.surname)).slice(0, meta.limits.healthCareTeam).map(person),
  };
}

export type GgopPlanBody = ReturnType<typeof ggopPlanBody>;

/**
 * Mandatory fields still missing (same rules as Communicate's ggopMissingFields; the
 * labels are Communicate's). Communicate checks again and refuses with the same list.
 */
export function ggopMissing(body: GgopPlanBody, meta: GgopFormMeta): { key: string; label: string }[] {
  const b = body as GgopPlanBody & { receiver?: ReturnType<typeof party> };
  const present = (key: string): boolean => {
    switch (key) {
      case 'receiver':
        return !!b.receiver && filled(b.receiver.ean) && filled(b.receiver.identifier) && filled(b.receiver.identifierCode);
      case 'sender':
        return filled(b.sender.ean) && filled(b.sender.identifier) && filled(b.sender.organisationName);
      case 'signedBy':
        return filled(b.signedBy.givenName) && filled(b.signedBy.surname) && filled(b.signedBy.title);
      case 'patient':
        return ('cpr' in b.patient && filled(b.patient.cpr as string)) && filled(b.patient.surname);
      case 'informationForPatientAndRelatives':
        return filled(b.informationForPatientAndRelatives);
      case 'consentInformed':
        return b.consentInformed === true;
      case 'currentHospitalContactStart':
        return filled(b.currentHospitalContact.start.date) && filled(b.currentHospitalContact.start.time);
      case 'currentHospitalContactEnd':
        return 'expectedEnd' in b.currentHospitalContact || 'expectedEndText' in b.currentHospitalContact;
      case 'patientHealthSummary':
        return filled(b.patientHealthSummary);
      case 'abilityAtDischargeSummary':
        return filled(b.abilityAtDischargeSummary);
      case 'abilityAtDischargeExtended':
        return 'abilityAtDischargeExtended' in b;
      case 'rehabilitationNeedAndPotential':
        return filled(b.rehabilitationNeedAndPotential);
      case 'actionDiagnosis':
        return 'icd10' in b;
      case 'contactMunicipality': {
        const c = b.contactMunicipality;
        return filled(c.departmentName) && filled(c.streetName) && filled(c.districtName) && filled(c.postCodeIdentifier) && filled(c.phone);
      }
      case 'municipalityReceiverOrCopy':
        return b.receiver?.identifierCode === 'kommunenummer' || b.ccReceivers.some((c) => c.identifierCode === 'kommunenummer');
      default:
        return true;
    }
  };
  return (meta.requiredFields[body.planType] ?? []).filter((k) => !present(k)).map((key) => ({ key, label: meta.fieldLabels[key] ?? key }));
}

/** File name for 'Hent XML' (GGOP-S 3.4.35 etc.: GGOP_A_3.4.35_A_GGOP1.xml is typed by the tester). */
export function ggopFileName(statisticalCode: string, letterIdentifier: string): string {
  return `${statisticalCode || 'GGOP'}_${letterIdentifier}.xml`;
}
